import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Save, CheckCircle2, XCircle, Send, PackageOpen,
  Users, Gift, AlertTriangle, Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_BADGE = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  pending_approval: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", cls: "bg-blue-100 text-blue-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  distributed: { label: "Distributed", cls: "bg-emerald-100 text-emerald-700" },
};

const TIER_COLORS = {
  A: "bg-purple-50 border-purple-200 text-purple-700",
  B: "bg-blue-50 border-blue-200 text-blue-700",
  C: "bg-emerald-50 border-emerald-200 text-emerald-700",
  D: "bg-amber-50 border-amber-200 text-amber-800",
};

const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

export default function FestivalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [festival, setFestival] = useState(null);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [decisionDialog, setDecisionDialog] = useState(null); // 'approve' | 'reject' | null
  const [decisionRemarks, setDecisionRemarks] = useState("");
  const [showDistribute, setShowDistribute] = useState(false);

  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const isHR = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const isApprover = user?.role === "super_admin" || user?.role === "senior_management";

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/festivals/${id}`);
      setFestival(data);
      if (data.status === "distributed" || data.status === "approved") {
        const { data: dists } = await api.get(`/festivals/${id}/distributions`);
        setDistributions(dists || []);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const updateField = (path, value) => {
    setFestival((prev) => {
      const next = { ...prev };
      if (Array.isArray(path)) {
        const [a, b] = path;
        next[a] = { ...(next[a] || {}), [b]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/festivals/${id}`, {
        name: festival.name,
        festival_date: festival.festival_date,
        description: festival.description,
        total_budget: parseFloat(festival.total_budget) || 0,
        tier_cash_amounts: festival.tier_cash_amounts,
        tier_voucher_amounts: festival.tier_voucher_amounts,
        voucher_brand: festival.voucher_brand,
        physical_gift_description: festival.physical_gift_description,
      });
      toast.success("Saved");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const submitForApproval = async () => {
    if (!window.confirm("Submit for Senior Management approval? You won't be able to edit after this.")) return;
    try {
      await api.post(`/festivals/${id}/submit-approval`);
      toast.success("Submitted to Senior Management");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    }
  };

  const submitDecision = async () => {
    if (decisionDialog === "reject" && (!decisionRemarks || decisionRemarks.length < 5)) {
      toast.error("Rejection reason required (min 5 chars)");
      return;
    }
    try {
      await api.post(`/festivals/${id}/${decisionDialog}`, { remarks: decisionRemarks });
      toast.success(`Festival ${decisionDialog}ed`);
      setDecisionDialog(null);
      setDecisionRemarks("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };

  const distribute = async () => {
    try {
      const { data } = await api.post(`/festivals/${id}/distribute`);
      toast.success(`Distributed to ${data.employees_distributed} employees · Total ₹${data.total_actual_spend.toLocaleString("en-IN")}`);
      setShowDistribute(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Distribute failed");
    }
  };

  const deleteFestival = async () => {
    if (!window.confirm("Delete this festival? This cannot be undone.")) return;
    try {
      await api.delete(`/festivals/${id}`);
      toast.success("Deleted");
      navigate("/admin/festivals");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading || !festival) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const editable = ["draft", "rejected"].includes(festival.status);
  const badge = STATUS_BADGE[festival.status] || STATUS_BADGE.draft;
  const totalPerHead = (t) =>
    (parseFloat(festival.tier_cash_amounts?.[t] || 0) || 0) +
    (parseFloat(festival.tier_voucher_amounts?.[t] || 0) || 0);

  return (
    <div className="space-y-4 max-w-6xl" data-testid="festival-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/festivals")}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Festival #{festival.year}</div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: "Chivo" }}>
            🎉 {festival.name}
            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${badge.cls}`} data-testid="status-badge">{badge.label}</span>
          </h1>
        </div>

        {/* Action buttons by state */}
        {isHR && editable && (
          <>
            <Button onClick={save} disabled={saving} variant="outline" size="sm" data-testid="save-btn">
              <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={submitForApproval} className="brand-bg brand-bg-hover text-white" size="sm" data-testid="submit-approval-btn">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Submit for Approval
            </Button>
            <Button onClick={deleteFestival} variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="delete-btn">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        {isApprover && festival.status === "pending_approval" && (
          <>
            <Button onClick={() => { setDecisionDialog("reject"); setDecisionRemarks(""); }} variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="reject-btn">
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
            </Button>
            <Button onClick={() => { setDecisionDialog("approve"); setDecisionRemarks(""); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="approve-btn">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve
            </Button>
          </>
        )}
        {isHR && festival.status === "approved" && (
          <Button onClick={() => setShowDistribute(true)} className="brand-bg brand-bg-hover text-white" size="sm" data-testid="distribute-btn">
            <PackageOpen className="h-3.5 w-3.5 mr-1.5" /> Distribute Now
          </Button>
        )}
      </div>

      {/* Status banner */}
      {festival.status === "rejected" && festival.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="rejection-banner">
          <b>Rejected by {festival.rejected_by_name}:</b> {festival.rejection_reason}
        </div>
      )}
      {festival.over_budget && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center gap-2" data-testid="overbudget-banner">
          <AlertTriangle className="h-4 w-4" />
          <span>Estimated spend <b>{fmtINR(festival.total_estimated)}</b> exceeds budget <b>{fmtINR(festival.total_budget)}</b>!</span>
        </div>
      )}

      {/* Budget summary */}
      <div className="grid md:grid-cols-4 gap-3">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Budget</div>
          <div className="text-xl font-bold mt-1" style={{ fontFamily: "Chivo" }}>{fmtINR(festival.total_budget)}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-red-700">Estimated Spend</div>
          <div className="text-xl font-bold text-red-700 mt-1" style={{ fontFamily: "Chivo" }} data-testid="est-spend">{fmtINR(festival.total_estimated)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{festival.budget_utilization_pct}% utilization</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Actual Spend</div>
          <div className="text-xl font-bold text-emerald-700 mt-1" style={{ fontFamily: "Chivo" }}>{fmtINR(festival.total_actual)}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-blue-700">Employees Covered</div>
          <div className="text-xl font-bold text-blue-700 mt-1" style={{ fontFamily: "Chivo" }}>
            {Object.values(festival.tier_summary || {}).reduce((s, t) => s + t.headcount, 0)}
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <h2 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5">
          <Gift className="h-3.5 w-3.5 text-red-600" /> Festival Setup
        </h2>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Name</Label>
            <Input value={festival.name} onChange={(e) => updateField("name", e.target.value)} disabled={!editable || !isHR} className="mt-1 h-8 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
            <Input type="date" value={festival.festival_date || ""} onChange={(e) => updateField("festival_date", e.target.value)} disabled={!editable || !isHR} className="mt-1 h-8 text-sm" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Total Budget (₹)</Label>
            <Input type="number" step="1000" value={festival.total_budget || 0} onChange={(e) => updateField("total_budget", e.target.value)} disabled={!editable || !isHR} className="mt-1 h-8 text-sm" />
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Description</Label>
          <Textarea value={festival.description || ""} onChange={(e) => updateField("description", e.target.value)} disabled={!editable || !isHR} rows={2} className="mt-1 text-sm" />
        </div>

        {/* Tier amounts */}
        <h3 className="text-sm font-bold text-slate-700 mt-5 mb-2 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-red-600" /> Per-Tier Gift Configuration
        </h3>
        <div className="grid md:grid-cols-4 gap-3">
          {["A", "B", "C", "D"].map((t) => {
            const tierData = festival.tier_summary?.[t] || {};
            return (
              <div key={t} className={`border rounded-lg p-3 ${TIER_COLORS[t]}`} data-testid={`tier-card-${t}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold">
                  Tier {t}
                </div>
                <div className="text-[10px] opacity-75 mb-2">{tierData.label?.split("—")[1]?.trim()}</div>
                <div className="text-xs">
                  <Label className="text-[9px] uppercase tracking-wider">Cash ₹</Label>
                  <Input
                    type="number"
                    step="100"
                    value={festival.tier_cash_amounts?.[t] || 0}
                    onChange={(e) => updateField("tier_cash_amounts", { ...festival.tier_cash_amounts, [t]: parseFloat(e.target.value) || 0 })}
                    disabled={!editable || !isHR}
                    className="mt-0.5 h-7 text-xs"
                    data-testid={`cash-${t}`}
                  />
                </div>
                <div className="text-xs mt-1.5">
                  <Label className="text-[9px] uppercase tracking-wider">Voucher ₹</Label>
                  <Input
                    type="number"
                    step="100"
                    value={festival.tier_voucher_amounts?.[t] || 0}
                    onChange={(e) => updateField("tier_voucher_amounts", { ...festival.tier_voucher_amounts, [t]: parseFloat(e.target.value) || 0 })}
                    disabled={!editable || !isHR}
                    className="mt-0.5 h-7 text-xs"
                  />
                </div>
                <div className="mt-2 pt-2 border-t border-current/20 text-[10px] space-y-0.5">
                  <div className="flex justify-between"><span>Per head:</span><b>{fmtINR(totalPerHead(t))}</b></div>
                  <div className="flex justify-between"><span>Headcount:</span><b>{tierData.headcount || 0}</b></div>
                  <div className="flex justify-between font-bold"><span>Tier Total:</span><span>{fmtINR(tierData.tier_total_estimate)}</span></div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Voucher Brand</Label>
            <Input
              placeholder="e.g., Amazon Pay, Flipkart"
              value={festival.voucher_brand || ""}
              onChange={(e) => updateField("voucher_brand", e.target.value)}
              disabled={!editable || !isHR}
              className="mt-1 h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Physical Gift Description</Label>
            <Input
              placeholder="e.g., Sweet box + diya set"
              value={festival.physical_gift_description || ""}
              onChange={(e) => updateField("physical_gift_description", e.target.value)}
              disabled={!editable || !isHR}
              className="mt-1 h-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Distributions table */}
      {festival.status === "distributed" && distributions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-slate-100 flex items-center gap-2">
            <PackageOpen className="h-4 w-4 text-emerald-600" />
            <h2 className="text-sm font-bold text-slate-700">Distribution Records ({distributions.length})</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-2 font-semibold">Punch ID</th>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Tier</th>
                <th className="px-3 py-2 font-semibold text-right">Cash</th>
                <th className="px-3 py-2 font-semibold text-right">Voucher</th>
                <th className="px-3 py-2 font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {distributions.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`dist-row-${d.id}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{d.punch_id || "—"}</td>
                  <td className="px-3 py-1.5 font-semibold">{d.employee_name}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold ${TIER_COLORS[d.tier]}`}>
                      Tier {d.tier}{d.tier_was_auto_suggested && " (auto)"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right">{fmtINR(d.cash_amount)}</td>
                  <td className="px-3 py-1.5 text-right">{fmtINR(d.voucher_amount)}</td>
                  <td className="px-3 py-1.5 text-right font-bold text-emerald-700">{fmtINR(d.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Decision dialog */}
      <Dialog open={!!decisionDialog} onOpenChange={(v) => !v && setDecisionDialog(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>{decisionDialog === "approve" ? "Approve Festival" : "Reject Festival"}</DialogTitle>
          </DialogHeader>
          <div>
            <p className="text-sm text-slate-700 mb-2">
              {decisionDialog === "approve"
                ? `Approve "${festival.name}" with estimated spend ${fmtINR(festival.total_estimated)}? HR can then distribute.`
                : "Reason for rejection (HR will see this):"}
            </p>
            <Textarea
              value={decisionRemarks}
              onChange={(e) => setDecisionRemarks(e.target.value)}
              rows={3}
              placeholder={decisionDialog === "approve" ? "Optional remarks" : "Please specify what to fix (min 5 chars)"}
              data-testid="decision-remarks"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(null)}>Cancel</Button>
            <Button
              onClick={submitDecision}
              className={decisionDialog === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="decision-confirm-btn"
            >
              Confirm {decisionDialog === "approve" ? "Approval" : "Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Distribute dialog */}
      <Dialog open={showDistribute} onOpenChange={setShowDistribute}>
        <DialogContent data-testid="distribute-dialog">
          <DialogHeader>
            <DialogTitle>Distribute {festival.name}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>This will create distribution records for all eligible employees per their tier configuration.</p>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs">
              <div className="flex justify-between mb-1"><span>Estimated total spend:</span><b>{fmtINR(festival.total_estimated)}</b></div>
              <div className="flex justify-between"><span>Total employees:</span><b>{Object.values(festival.tier_summary || {}).reduce((s, t) => s + t.headcount, 0)}</b></div>
            </div>
            <p className="text-xs text-slate-500">After distribution, you can export the list for accounting.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDistribute(false)}>Cancel</Button>
            <Button onClick={distribute} className="brand-bg brand-bg-hover text-white" data-testid="distribute-confirm-btn">
              <PackageOpen className="h-3.5 w-3.5 mr-1.5" /> Confirm Distribution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
